import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { toast } from "@msuite/ui/toast";
import type { ProjectTimelineNoteWithFiles } from "@msuite/utils/types";
import React from "react";
import { useCitadel } from "@/hooks/use-citadel";
import { useProject } from "@/hooks/use-project";
import { client } from "@/hono";
import { EditProjectTimelineNoteModal } from "@/modals/projects/edit-project-timeline-note-modal";

/** Props Interface */
interface ProjectTimelineNoteItemMenuProps {
	item: ProjectTimelineNoteWithFiles;
}

export function ProjectTimelineNoteItemMenu({ item }: ProjectTimelineNoteItemMenuProps) {
	/** Hooks */
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const qc = useQueryClient();
	const editNoteModal = useDisclosure();

	/** Query */
	const { citadel } = useCitadel();
	const { project } = useProject({ projectId: item.projectId });

	/** Functions */
	async function handleOnDeleteNote() {
		try {
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.projects[":projectId"].timeline.notes[":noteId"].$delete({
				param: {
					projectId: item.projectId,
					noteId: item.id,
				},
			});
			await invalidateTimeline();
			toast.success(t("general.deleted_successfully"));
		} catch (error) {
			console.error(error);
		}
	}

	async function invalidateTimeline() {
		await qc.invalidateQueries({
			predicate: invalidate(["timeline"]),
		});
	}

	/** Computed */
	// A diary entry hangs off the project, so changing or removing one is changing the site it
	// hangs off — bound against that row, because `update Project` also reaches only the sites its
	// holder runs. Same three-state guard as `Can` documents; when the notes domain's subject lands
	// this becomes an `own` check on who wrote the entry (decision 27).
	const writeIsDenied =
		!!citadel && !!project && citadel.cannot("update", "Project", { ...project });

	/** Render */
	return (
		<React.Fragment>
			<div className="-m-2">
				{!writeIsDenied && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editNoteModal.onOpen,
							onDelete: handleOnDeleteNote,
						})}
					>
						<Button
							variant="ghost"
							size="xxs"
							icon={<Icon name="EllipsisVertical" />}
						/>
					</Menu>
				)}
			</div>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_deletion")}
				/>
			)}
			{editNoteModal.isOpen && (
				<EditProjectTimelineNoteModal
					isOpen={editNoteModal.isOpen}
					onClose={editNoteModal.onClose}
					onComplete={invalidateTimeline}
					projectId={item.projectId}
					noteId={item.id}
				/>
			)}
		</React.Fragment>
	);
}
